import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, Badge, Button, Card, CardHeader, DotsVertical, Edit01, Menu, Plus, Table, Trash01 } from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import PromoCodeForm from "../../ConfirmTicketing/components/PromoCodeForm";
import DecisionModal from "../../ClassesShared/DecisionModal";
import { formatRegisteredAt } from "../../ClassesShared/classLabels";
import {
  DATE_AND_TIME_ONLY,
  STATUS_BADGES,
  blankClassPromoCode,
  classPromoCodePayload,
  classPromoCodeToForm,
  discountLabel,
  usesLabel,
} from "../classPromoCodes";

const APPLIES_TO_LABELS = { class: "This class", template: "Every class from this template" };
const NO_RECORD = Object.freeze({});
const NO_ITEMS = Object.freeze([]);

export const PROMO_CODE_NAME_HINT = "Letters, numbers and - _ % ! @ . + only. Spaces and other characters are left out, so check the code above before saving.";

export default function PromotionsTab({ promoCodesPath, csrfToken, canManage, currencyCode, timeZone }) {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [working, setWorking] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(promoCodesPath, { params: { format: "json" } });
      setPromoCodes(resp.data);
      setError(null);
    } catch (e) {
      setError("Could not load promo codes.");
    } finally {
      setLoading(false);
    }
  }, [promoCodesPath]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (deletedCount > 0 && listRef.current) listRef.current.focus();
  }, [deletedCount]);

  const headers = { "X-CSRF-Token": csrfToken };

  const save = async (form) => {
    const body = { class_promo_code: classPromoCodePayload(form, timeZone) };
    try {
      const resp = editing.promoCode
        ? await axios.patch(`${promoCodesPath}/${editing.promoCode.id}`, body, { headers })
        : await axios.post(promoCodesPath, { ...body, applies_to: editing.appliesTo }, { headers });
      toastSuccess(`Saved promo code ${resp.data.name}.`);
      setEditing(null);
      await load();
    } catch (e) {
      toastError((e.response?.data?.errors || ["Could not save the promo code."]).join(" "));
    }
  };

  const remove = async () => {
    setWorking(true);
    try {
      await axios.delete(`${promoCodesPath}/${deleting.id}`, { headers });
      toastSuccess(`Deleted promo code ${deleting.name}.`);
      setDeleting(null);
      setDeletedCount((count) => count + 1);
      await load();
    } catch (e) {
      toastError((e.response?.data?.errors || ["Could not delete the promo code."]).join(" "));
    } finally {
      setWorking(false);
    }
  };

  const columns = [
    { key: "name", header: "Code", primary: true, render: (promoCode) => <code>{promoCode.name}</code> },
    { key: "discount", header: "Discount", render: (promoCode) => discountLabel(promoCode, currencyCode) },
    { key: "applies_to", header: "Applies to", render: (promoCode) => APPLIES_TO_LABELS[promoCode.applies_to] },
    { key: "uses", header: "Uses", render: usesLabel },
    {
      key: "window",
      header: "Redeemable",
      render: (promoCode) => `${formatRegisteredAt(promoCode.start_time, timeZone)} – ${formatRegisteredAt(promoCode.end_time, timeZone)}`,
    },
    {
      key: "status",
      header: "Status",
      render: (promoCode) => {
        const badge = STATUS_BADGES[promoCode.status];
        return <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>;
      },
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (promoCode) => canManage && (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${promoCode.name}`} />}
          items={[
            { label: "Edit", icon: Edit01, onClick: () => setEditing({ promoCode, form: classPromoCodeToForm(promoCode, timeZone) }) },
            promoCode.deletable && { label: "Delete", icon: Trash01, onClick: () => setDeleting(promoCode) },
          ].filter(Boolean)}
        />
      ),
    },
  ];

  const addMenu = canManage && (
    <Menu
      placement="bottom-end"
      trigger={<Button variant="secondary" icon={Plus}>Add Promo Code</Button>}
      items={[
        { label: "For this class", onClick: () => setEditing({ appliesTo: "class", form: blankClassPromoCode(timeZone) }) },
        { label: "For every class from this template", onClick: () => setEditing({ appliesTo: "template", form: blankClassPromoCode(timeZone) }) },
      ]}
    />
  );

  if (error) return <Alert color="error" role="alert" description={error} />;

  return (
    <div>
      <div ref={listRef} tabIndex={-1} className="classes-focus-target">
        <Card
          padding="none"
          header={(
            <CardHeader
              title="Promo Codes"
              supportingText="Codes people can enter at checkout for a discount. When a code saves less than the group discount, the group discount applies instead."
              actions={addMenu}
            />
          )}
        >
          <Table className="classes-table"
            columns={columns}
            data={promoCodes}
            loading={loading}
            getRowKey={(promoCode) => promoCode.id}
            stackBelow={720}
            empty="No promo codes for this class yet."
          />
        </Card>
      </div>

      <PromoCodeForm
        isOpen={Boolean(editing)}
        setIsOpen={() => setEditing(null)}
        onClose={() => setEditing(null)}
        currentUser={NO_RECORD}
        confirm={NO_RECORD}
        csrfToken={csrfToken}
        team={NO_RECORD}
        promoCodeToCopy={NO_RECORD}
        promoCodeToEdit={NO_RECORD}
        showActivities={NO_ITEMS}
        ticketTypes={NO_ITEMS}
        initialPromoCode={editing?.form}
        onSubmitPromoCode={save}
        nameHint={PROMO_CODE_NAME_HINT}
        discountOnly
        availabilityOptions={DATE_AND_TIME_ONLY}
      />

      {deleting && (
        <DecisionModal
          type="error"
          icon={Trash01}
          title={`Delete promo code ${deleting.name}?`}
          description="People can no longer use this code at checkout. Registrations that already used it keep their discount."
          cancelLabel="Keep Code"
          confirmLabel={working ? "Deleting…" : "Delete Code"}
          working={working}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

PromotionsTab.propTypes = {
  promoCodesPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  currencyCode: PropTypes.string,
  timeZone: PropTypes.string,
};
